import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, IconButton, Tooltip } from '@mui/material';
import {
  Hand as HandIcon,
  Minus as MinusIcon,
  Plus as PlusIcon,
  RotateCcw as ResetIcon,
  X as CloseIcon,
} from 'lucide-react';
import styles from './MermaidDiagram.module.scss';

interface MermaidFullscreenViewerProps {
  open: boolean;
  svg: string;
  onClose: () => void;
}

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export default function MermaidFullscreenViewer({
  open,
  svg,
  onClose,
}: MermaidFullscreenViewerProps) {
  const [scale, setScale] = useState(1);
  const [isPanEnabled, setIsPanEnabled] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const scaledRef = useRef<HTMLDivElement | null>(null);

  const effectivePan = isPanEnabled || isSpacePressed;
  const canZoomIn = scale < MAX_ZOOM;
  const canZoomOut = scale > MIN_ZOOM;

  const cursorClass = useMemo(() => {
    if (!effectivePan) return '';
    return isDragging ? styles.isGrabbing : styles.isGrab;
  }, [effectivePan, isDragging]);

  const handleReset = () => {
    setScale(1);
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollTo({ top: 0, left: 0 });
  };

  const handleZoom = (delta: number) => {
    setScale((prev) => clamp(Number((prev + delta).toFixed(2)), MIN_ZOOM, MAX_ZOOM));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!effectivePan) return;
    event.preventDefault();
    setIsDragging(true);
    const viewport = viewportRef.current;
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: viewport?.scrollLeft ?? 0,
      top: viewport?.scrollTop ?? 0,
    };
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartRef.current) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    const deltaX = event.clientX - dragStartRef.current.x;
    const deltaY = event.clientY - dragStartRef.current.y;
    viewport.scrollLeft = dragStartRef.current.left - deltaX;
    viewport.scrollTop = dragStartRef.current.top - deltaY;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragStartRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      setIsSpacePressed(false);
      setIsDragging(false);
      dragStartRef.current = null;
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) {
        const key = event.key;
        if (key === '+' || key === '=' || key === '-' || key === '0') {
          event.preventDefault();
        }
      }
      if (event.code === 'Space') {
        event.preventDefault();
        setIsSpacePressed(true);
        return;
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        handleZoom(ZOOM_STEP);
        return;
      }
      if (event.key === '-') {
        event.preventDefault();
        handleZoom(-ZOOM_STEP);
        return;
      }
      if (event.key === '0') {
        event.preventDefault();
        handleReset();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') setIsSpacePressed(false);
    };

    const onWindowWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
      }
    };

    const onGesture = (event: Event) => {
      event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('wheel', onWindowWheel, {
      passive: false,
    });
    window.addEventListener('gesturestart', onGesture, {
      passive: false,
    } as AddEventListenerOptions);
    window.addEventListener('gesturechange', onGesture, {
      passive: false,
    } as AddEventListenerOptions);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('wheel', onWindowWheel);
      window.removeEventListener('gesturestart', onGesture as EventListenerOrEventListenerObject);
      window.removeEventListener('gesturechange', onGesture as EventListenerOrEventListenerObject);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      if (event.deltaY < 0) handleZoom(ZOOM_STEP);
      else handleZoom(-ZOOM_STEP);
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    handleReset();
  }, [open, svg]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <div className={styles.fullscreenRoot}>
        <div className={styles.fullscreenToolbar}>
          <div className={styles.fullscreenControls}>
            <Tooltip title="Перемещение (или зажмите Space)">
              <IconButton
                size="small"
                onClick={() => setIsPanEnabled((prev) => !prev)}
                className={`${styles.viewerBtn} ${isPanEnabled ? styles.viewerBtnActive : ''}`}
                aria-label="Включить режим перемещения"
              >
                <HandIcon size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Уменьшить">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleZoom(-ZOOM_STEP)}
                  className={styles.viewerBtn}
                  aria-label="Уменьшить диаграмму"
                  disabled={!canZoomOut}
                >
                  <MinusIcon size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Увеличить">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleZoom(ZOOM_STEP)}
                  className={styles.viewerBtn}
                  aria-label="Увеличить диаграмму"
                  disabled={!canZoomIn}
                >
                  <PlusIcon size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Сбросить масштаб и позицию (0)">
              <IconButton
                size="small"
                onClick={handleReset}
                className={styles.viewerBtn}
                aria-label="Сбросить вид диаграммы"
              >
                <ResetIcon size={16} />
              </IconButton>
            </Tooltip>
            <div className={styles.zoomValue}>{Math.round(scale * 100)}%</div>
          </div>
          <Tooltip title="Закрыть">
            <IconButton
              size="small"
              onClick={onClose}
              className={styles.viewerBtn}
              aria-label="Закрыть полноэкранный просмотр"
            >
              <CloseIcon size={16} />
            </IconButton>
          </Tooltip>
        </div>

        <div
          ref={viewportRef}
          className={`${styles.fullscreenViewport} ${cursorClass}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className={styles.fullscreenCanvas}>
            <div
              ref={scaledRef}
              className={styles.fullscreenScaled}
              style={{
                transform: `scale(${scale})`,
                userSelect: isDragging ? 'none' : 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
